import { EventLabel } from "src/composables/InleagueApiV1.Event"
import { FK_nodeRef, FK_validation_strlen, nextGlobalIntlike, useAutoFocusOnMount, vReqT } from "src/helpers/utils"
import { defineComponent, ref } from "vue"
import { FormKit } from "@formkit/vue"
import { Btn2, btn2_redEnabledClasses } from "../UserInterface/Btn2"
import { FkDynamicValidation } from "src/helpers/FkUtils"
import { Int } from "src/interfaces/InleagueApiV1"

export const nextLocalEventLabelID = (() => {
  let v = -1;
  return () => v--;
})();

export interface EventLabelFormData {
  __vueKey: string,
  tentative: boolean,
  /**
   * should be negative integer if tentative=true, otherwise the actual ID of an object in the DB
   */
  eventLabelID: Int,
  label: string,
  labeledSignupsCount: number,
  markedForDelete: boolean,
  busy: boolean,
}

export function EventLabelCanonicalID(v: string | {label: string}) {
  return typeof v === "string"
    ? v.trim().toLowerCase()
    : v.label.trim().toLowerCase()
}

export const EventLabelEditor = defineComponent({
  props: {
    labels: vReqT<EventLabelFormData[]>(),
    addLabel: vReqT<(_label: string) => void | Promise<void>>(),
  },
  setup(props, ctx) {
    const text = ref("")

    const fkbs_elemDataID = `data-event-label-editor-${nextGlobalIntlike()}`
    const fkbs_renderKey = ref(0)
    const fkbs_textInputRef = FK_nodeRef()
    const fkbs_getTextElem = () => document.querySelector<HTMLElement>(`[${fkbs_elemDataID}]`)
    const lastErr = ref("")


    const handleAddOne = () : void => {
      if (text.value.trim() === "") {
        return;
      }

      const canonical = EventLabelCanonicalID(text.value)

      if (canonical === "") {
        return;
      }
      else if (props.labels.find(v => EventLabelCanonicalID(v) === canonical)) {
        lastErr.value = "Label already exists"
      }
      else {
        props.addLabel(text.value.trim()) // n.b. NOT the "canonical" value, but the actual user input value
        setTimeout(() => {
          text.value = ""
          fkbs_renderKey.value += 1
          setTimeout(() => fkbs_getTextElem()?.focus(), 0);
        })
      }
    }

    return () => {
      return <div style="--fk-margin-outer:none;">
        <div class={`flex gap-2 items-center mb-2`}>
          <FormKit type="form" actions={false} onSubmit={handleAddOne}>
            <div class="flex items-center gap-2">
              <FormKit
                type="text"
                key={fkbs_renderKey.value}
                v-model={text.value}
                {...{[fkbs_elemDataID]: ""}}
                ref={fkbs_textInputRef}
                onInput={() => {
                  lastErr.value = ""
                }}
                data-test="newLabel-input"
              />
              <Btn2 type="submit" class="px-2 py-1">
                Add
              </Btn2>
            </div>
          </FormKit>
        </div>
        <div class="text-red-600 text-sm">{lastErr.value}</div>
      </div>
    }
  }
})

export const EditEventLabelModal = defineComponent({
  props: {
    existingLabels: vReqT<EventLabelFormData[]>(),
    label: vReqT<EventLabelFormData>(),
  },
  emits: {
    submit: (_freshText: string) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const text = ref(props.label.label)
    const ValidateDistinctLabels = FkDynamicValidation(() => {
      if (props.label.label.trim().toLowerCase() === text.value.trim().toLowerCase()) {
        return {status: "valid"} // hasn't been changed, that's ok
      }

      const a = EventLabelCanonicalID(text.value)
      return props.existingLabels.find(b => a === EventLabelCanonicalID(b))
        ? {status: "invalid", msg: "Label already exists"}
        : {status: "valid"}
    })

    const autofocus = useAutoFocusOnMount()

    return () => {
      return <div>
        <FormKit type="form" actions={false} onSubmit={() => ctx.emit("submit", text.value)}>
          <FormKit
            type="text"
            v-model={text.value}
            validation={[["required"], FK_validation_strlen(1, STRLEN_EVENT_LABEL)]}
            validationLabel="Value"
            {...autofocus}
          />
          <ValidateDistinctLabels/>
          <div class="flex gap-2 items-center">
            <Btn2 type="submit" class="px-2 py-1">OK</Btn2>
            <Btn2 class="px-2 py-1" enabledClasses={btn2_redEnabledClasses} onClick={() => ctx.emit("cancel")}>Cancel</Btn2>
          </div>
        </FormKit>
      </div>
    }
  }
})

const STRLEN_EVENT_LABEL = 500

export function eventLabelFormFromExisting(v: EventLabel) : EventLabelFormData {
  return {
    __vueKey: nextGlobalIntlike(),
    tentative: false,
    eventLabelID: v.eventSignupLabelID,
    label: v.label,
    labeledSignupsCount: v.labeledSignupsCount,
    busy: false,
    markedForDelete: false,
  }
}
